import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Tabs,
  Tab,
  Stack,
} from "@mui/material";
import { Delete, Edit, Save, Close, Refresh, Add } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import AppPagination from "components/AppPagination";
import SearchInput from "components/input-fields/SearchInput";
import FlexBox from "components/flexbox/FlexBox";

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const DiseaseAdvisory = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Loading state
  const [loading, setLoading] = useState(false);

  // Dropdown data
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingFaqs, setLoadingFaqs] = useState(false);

  // Category CRUD states
  const [categoryItems, setCategoryItems] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryEditingId, setCategoryEditingId] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: "" });
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryDeletingId, setCategoryDeletingId] = useState(null);

  // Subcategory CRUD states
  const [subcategoryItems, setSubcategoryItems] = useState([]);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [subcategoryEditingId, setSubcategoryEditingId] = useState(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState({ name: "", category_id: "", sequence: "" });
  const [subcategorySaving, setSubcategorySaving] = useState(false);
  const [subcategoryDeletingId, setSubcategoryDeletingId] = useState(null);

  // FAQ CRUD states
  const [faqItems, setFaqItems] = useState([]);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [faqEditingId, setFaqEditingId] = useState(null);
  const [faqFormData, setFaqFormData] = useState({ question: "", category_id: "", subcategory_id: "" });
  const [faqSaving, setFaqSaving] = useState(false);
  const [faqDeletingId, setFaqDeletingId] = useState(null);
  
  // FAQ Pagination states
  const [faqPage, setFaqPage] = useState(1);
  const [faqPageSize, setFaqPageSize] = useState(100);
  const [faqTotalCount, setFaqTotalCount] = useState(0);
  
  // FAQ Search state
  const [faqSearchQuery, setFaqSearchQuery] = useState("");

  useEffect(() => {
    setPageTitle("Disease Advisory");
  }, [setPageTitle]);

  useEffect(() => {
    fetchCategories();
    if (currentTab === 0) {
      fetchCategoryItems();
    } else if (currentTab === 1) {
      fetchSubcategoryItems();
    } else if (currentTab === 2) {
      // Reset to first page when switching to FAQ tab, then fetch
      if (faqPage !== 1) {
        setFaqPage(1);
      } else {
        fetchFaqItems(1, faqPageSize);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]); // Only depend on currentTab, not setPageTitle

  // Fetch FAQs when page or page size changes (only if on FAQ tab)
  useEffect(() => {
    if (currentTab === 2) {
      fetchFaqItems(faqPage, faqPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqPage, faqPageSize]);

  const userId = localStorage.getItem("user_id") || "1";

  // Fetch categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_categories}?place=disease_advisory&user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const categoryList = Array.isArray(response.data)
          ? response.data
          : response.data.categories || response.data.Category || [];
        setCategories(categoryList.filter((cat) => !cat.is_deleted && cat.status === "Active"));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.showError("Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch subcategories by category
  const fetchSubcategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    setLoadingSubcategories(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_subcategories_by_category}?category_id=${categoryId}&user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const subCategoryList = Array.isArray(response.data)
          ? response.data
          : response.data.subcategories || response.data.SubCategory || [];
        setSubcategories(subCategoryList);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.showError("Failed to load subcategories");
    } finally {
      setLoadingSubcategories(false);
    }
  }, [userId, toast]);

  // Fetch subcategories when category_id changes in FAQ form (for edit mode)
  useEffect(() => {
    if (faqDialogOpen && faqFormData.category_id) {
      fetchSubcategories(Number(faqFormData.category_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqDialogOpen, faqFormData.category_id]); // Removed fetchSubcategories to prevent unnecessary re-renders

  // Fetch FAQs by category and subcategory
  const fetchFaqs = useCallback(async (categoryId, subCategoryId) => {
    if (!categoryId || !subCategoryId) {
      setFaqs([]);
      return;
    }
    setLoadingFaqs(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_disease_advisory_question_by_category}?category=${categoryId}&sub_category=${subCategoryId}`
      );
      if (response?.success && response?.data) {
        const faqList = Array.isArray(response.data)
          ? response.data
          : response.data.questions || response.data.DiseaseAdvisoryQuestion || [];
        setFaqs(faqList);
      } else {
        setFaqs([]);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      setFaqs([]);
    } finally {
      setLoadingFaqs(false);
    }
  }, []);


  // Fetch category items
  const fetchCategoryItems = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_categories}?place=disease_advisory&user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const categoryList = Array.isArray(response.data)
          ? response.data
          : response.data.categories || response.data.Category || [];
        setCategoryItems(categoryList);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.showError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategory items
  const fetchSubcategoryItems = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_all_subcategories}?user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const subCategoryList = Array.isArray(response.data)
          ? response.data
          : response.data.subcategories || response.data.SubCategory || [];
        setSubcategoryItems(subCategoryList);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.showError("Failed to load subcategories");
    } finally {
      setLoading(false);
    }
  };

  // Fetch FAQ items
  const fetchFaqItems = async (page = faqPage, pageSize = faqPageSize) => {
    setLoading(true);
    try {
      // Fetch FAQs with pagination params
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_disease_advisory_question_by_category}?user_id=${userId}&page=${page}&page_size=${pageSize}`
      );
      if (response?.success && response?.data) {
        const faqList = Array.isArray(response.data)
          ? response.data
          : response.data.questions || response.data.DiseaseAdvisoryQuestion || [];
        setFaqItems(faqList);
        
        // Handle total count - check if it's in the response
        if (response.data.count !== undefined) {
          setFaqTotalCount(response.data.count);
        } else if (response.data.total !== undefined) {
          setFaqTotalCount(response.data.total);
        } else if (response.data.total_count !== undefined) {
          setFaqTotalCount(response.data.total_count);
        } else {
          // If no total count provided, estimate based on current page
          // If we got a full page, there might be more
          if (faqList.length === pageSize) {
            setFaqTotalCount((page * pageSize) + 1); // At least this many
          } else {
            setFaqTotalCount((page - 1) * pageSize + faqList.length);
          }
        }
      } else {
        setFaqItems([]);
        setFaqTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.showError("Failed to load FAQs");
      setFaqItems([]);
      setFaqTotalCount(0);
    } finally {
      setLoading(false);
    }
  };


  // Category CRUD functions
  const handleCategoryOpenDialog = (item = null) => {
    if (item) {
      setCategoryEditingId(item.id);
      setCategoryFormData({
        name: item.name || "",
      });
    } else {
      setCategoryEditingId(null);
      setCategoryFormData({ name: "" });
    }
    setCategoryDialogOpen(true);
  };

  const handleCategoryCloseDialog = () => {
    setCategoryDialogOpen(false);
    setCategoryEditingId(null);
    setCategoryFormData({ name: "" });
  };

  const handleCategorySave = async () => {
    if (!categoryFormData.name?.trim()) {
      toast.showError("Category name is required");
      return;
    }

    setCategorySaving(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append('name', categoryFormData.name.trim());
      formDataPayload.append('user_id', userId);

      let response;
      if (categoryEditingId) {
        formDataPayload.append('category_id', categoryEditingId);
        response = await apiService.put(API_ENDPOINTS.PUT_update_category, formDataPayload, undefined, 'multipart/form-data');
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_add_category, formDataPayload, undefined, 'multipart/form-data');
      }

      if (response?.success) {
        toast.showSuccess(categoryEditingId ? "Category updated successfully" : "Category added successfully");
        handleCategoryCloseDialog();
        // Refresh the category list immediately
        await fetchCategoryItems();
        await fetchCategories(); // Refresh dropdown
      } else {
        toast.showError(response?.message || "Failed to save category");
      }
    } catch (error) {
      console.error("Error saving category:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save category";
      toast.showError(errorMessage);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Category",
      message: "Are you sure you want to delete this category?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setCategoryDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_category}?category_id=${id}&user_id=${userId}`
      );
      if (response?.success) {
        toast.showSuccess("Category deleted successfully");
        fetchCategoryItems();
        fetchCategories(); // Refresh dropdown
      } else {
        toast.showError(response?.message || "Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.showError("Failed to delete category");
    } finally {
      setCategoryDeletingId(null);
    }
  };

  // Subcategory CRUD functions
  const handleSubcategoryOpenDialog = (item = null) => {
    if (item) {
      setSubcategoryEditingId(item.id);
      setSubcategoryFormData({
        name: item.name || "",
        category_id: item.category_id || item.category?.id || "",
        sequence: item.sequence || "",
      });
    } else {
      setSubcategoryEditingId(null);
      setSubcategoryFormData({ name: "", category_id: "", sequence: "" });
    }
    setSubcategoryDialogOpen(true);
  };

  const handleSubcategoryCloseDialog = () => {
    setSubcategoryDialogOpen(false);
    setSubcategoryEditingId(null);
    setSubcategoryFormData({ name: "", category_id: "", sequence: "" });
  };

  const handleSubcategorySave = async () => {
    if (!subcategoryFormData.name?.trim() || !subcategoryFormData.category_id) {
      toast.showError("Subcategory name and category are required");
      return;
    }

    setSubcategorySaving(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append('name', subcategoryFormData.name.trim());
      formDataPayload.append('category_id', subcategoryFormData.category_id);
      if (subcategoryFormData.sequence) {
        formDataPayload.append('sequence', subcategoryFormData.sequence);
      }
      formDataPayload.append('user_id', userId);

      let response;
      if (subcategoryEditingId) {
        formDataPayload.append('sub_category_id', subcategoryEditingId);
        response = await apiService.put(API_ENDPOINTS.PUT_update_subcategory, formDataPayload, undefined, 'multipart/form-data');
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_add_subcategory, formDataPayload, undefined, 'multipart/form-data');
      }

      if (response?.success) {
        toast.showSuccess(subcategoryEditingId ? "Subcategory updated successfully" : "Subcategory added successfully");
        handleSubcategoryCloseDialog();
        fetchSubcategoryItems();
      } else {
        toast.showError(response?.message || "Failed to save subcategory");
      }
    } catch (error) {
      console.error("Error saving subcategory:", error);
      toast.showError("Failed to save subcategory");
    } finally {
      setSubcategorySaving(false);
    }
  };

  const handleSubcategoryDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Subcategory",
      message: "Are you sure you want to delete this subcategory?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setSubcategoryDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_subcategory}?sub_category_id=${id}&user_id=${userId}`
      );
      if (response?.success) {
        toast.showSuccess("Subcategory deleted successfully");
        fetchSubcategoryItems();
      } else {
        toast.showError(response?.message || "Failed to delete subcategory");
      }
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      toast.showError("Failed to delete subcategory");
    } finally {
      setSubcategoryDeletingId(null);
    }
  };

  // FAQ CRUD functions
  const handleFaqOpenDialog = (item = null) => {
    if (item) {
      const categoryId = item.category?.id ? String(item.category.id) : "";
      const subcategoryId = item.sub_category?.id ? String(item.sub_category.id) : "";
      
      // Ensure the category from the FAQ item is included in the categories list
      // (even if it's deleted/inactive, so it shows in the dropdown when editing)
      if (item.category && categoryId) {
        setCategories(prev => {
          const categoryExists = prev.some(cat => String(cat.id) === categoryId);
          if (!categoryExists) {
            // Add the category temporarily to the list so it appears in the dropdown
            return [...prev, item.category];
          }
          return prev;
        });
      }
      
      setFaqEditingId(item.id);
      setFaqFormData({
        question: item.question || item.prompt || "",
        category_id: categoryId,
        subcategory_id: subcategoryId,
      });
      
      // Fetch subcategories if category is present
      if (categoryId) {
        fetchSubcategories(Number(categoryId));
      }
    } else {
      setFaqEditingId(null);
      setFaqFormData({ question: "", category_id: "", subcategory_id: "" });
    }
    setFaqDialogOpen(true);
  };

  const handleFaqCloseDialog = () => {
    setFaqDialogOpen(false);
    setFaqEditingId(null);
    setFaqFormData({ question: "", category_id: "", subcategory_id: "" });
  };

  const handleFaqSave = async () => {
    if (!faqFormData.question?.trim() || !faqFormData.category_id || !faqFormData.subcategory_id) {
      toast.showError("Please fill all required fields");
      return;
    }

    setFaqSaving(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append('category', Number(faqFormData.category_id));
      formDataPayload.append('sub_category', Number(faqFormData.subcategory_id));
      formDataPayload.append('question', faqFormData.question.trim());

      let response;
      if (faqEditingId) {
        // Update FAQ
        formDataPayload.append('disease_advisory_question_id', faqEditingId);
        response = await apiService.put(
          API_ENDPOINTS.PUT_update_faq,
          formDataPayload,
          'multipart/form-data'
        );
        if (response?.success) {
          toast.showSuccess("FAQ updated successfully");
          handleFaqCloseDialog();
          fetchFaqItems(faqPage, faqPageSize);
        } else {
          toast.showError(response?.message || "Failed to update FAQ");
        }
      } else {
        // Add FAQ
        response = await apiService.post(
          API_ENDPOINTS.POST_add_faq,
          formDataPayload,
          'multipart/form-data'
        );

        if (response?.success) {
          toast.showSuccess("FAQ added successfully");
          handleFaqCloseDialog();
          // Reset to first page when adding new FAQ
          setFaqPage(1);
          fetchFaqItems(1, faqPageSize);
        } else {
          toast.showError(response?.message || "Failed to add FAQ");
        }
      }
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.showError(error?.message || "Failed to save FAQ");
    } finally {
      setFaqSaving(false);
    }
  };

  const handleFaqDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete FAQ",
      message: "Are you sure you want to delete this FAQ?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setFaqDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_faq}?disease_advisory_question_id=${id}`
      );
      if (response?.success) {
        toast.showSuccess("FAQ deleted successfully");
        // If current page becomes empty, go to previous page
        if (faqItems.length === 1 && faqPage > 1) {
          setFaqPage(faqPage - 1);
          fetchFaqItems(faqPage - 1, faqPageSize);
        } else {
          fetchFaqItems(faqPage, faqPageSize);
        }
      } else {
        toast.showError(response?.message || "Failed to delete FAQ");
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.showError(error?.message || "Failed to delete FAQ");
    } finally {
      setFaqDeletingId(null);
    }
  };


  // Render Category Tab
  const renderCategoryTab = () => (
    <>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Categories
          </Typography>
          <Muted variant="body2">Manage disease advisory categories</Muted>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => handleCategoryOpenDialog()}
          >
            Add Category
          </Button>
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchCategoryItems} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>

      <TableCard>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
              <TableRow>
                <StyledTableCell className="header" align="center" sx={{ width: '100px' }}>Sr No</StyledTableCell>
                <StyledTableCell className="header" align="center">Category Name</StyledTableCell>
                <StyledTableCell className="header" align="center" sx={{ width: '150px' }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : categoryItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No categories found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categoryItems.map((item, index) => (
                  <StyledTableRow key={item.id}>
                    <StyledTableCell align="center">
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontWeight: 500,
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body1" fontWeight={500} sx={{ color: 'text.primary' }}>
                        {item.name}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                        <Tooltip title="Edit">
                          <IconButton color="primary" size="small" onClick={() => handleCategoryOpenDialog(item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleCategoryDelete(item.id)}
                              disabled={categoryDeletingId === item.id}
                            >
                              {categoryDeletingId === item.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>
    </>
  );

  // Render Subcategory Tab
  const renderSubcategoryTab = () => (
    <>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Subcategories
          </Typography>
          <Muted variant="body2">Manage disease advisory subcategories</Muted>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={() => handleSubcategoryOpenDialog()}
          >
            Add Subcategory
          </Button>
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchSubcategoryItems} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>

      <TableCard>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
              <TableRow>
                <StyledTableCell className="header" align="center" sx={{ width: '100px' }}>Sr No</StyledTableCell>
                <StyledTableCell className="header" align="center">Subcategory</StyledTableCell>
                <StyledTableCell className="header" align="center">Category</StyledTableCell>
                <StyledTableCell className="header" align="center" sx={{ width: '150px' }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : subcategoryItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No subcategories found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subcategoryItems.map((item, index) => (
                  <StyledTableRow key={item.id}>
                    <StyledTableCell align="center">
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontWeight: 500,
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body1" fontWeight={500} sx={{ color: 'text.primary' }}>
                        {item.name}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {item.category?.name || item.category_name || "N/A"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                        <Tooltip title="Edit">
                          <IconButton color="primary" size="small" onClick={() => handleSubcategoryOpenDialog(item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleSubcategoryDelete(item.id)}
                              disabled={subcategoryDeletingId === item.id}
                            >
                              {subcategoryDeletingId === item.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>
    </>
  );

  // Handle FAQ page change
  const handleFaqPageChange = (event, value) => {
    setFaqPage(value);
  };

  // Filter FAQ items based on search query
  const filteredFaqItems = useMemo(() => {
    if (!faqSearchQuery.trim()) {
      return faqItems;
    }
    const query = faqSearchQuery.toLowerCase().trim();
    return faqItems.filter((item) => {
      const question = (item.question || item.prompt || "").toLowerCase();
      const category = (item.category_name || "").toLowerCase();
      const subcategory = (item.sub_category_name || "").toLowerCase();
      return (
        question.includes(query) ||
        category.includes(query) ||
        subcategory.includes(query)
      );
    });
  }, [faqItems, faqSearchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (faqSearchQuery && faqPage !== 1) {
      setFaqPage(1);
    }
  }, [faqSearchQuery]);

  // Render FAQ Tab
  const renderFaqTab = () => {
    const totalPages = Math.ceil(faqTotalCount / faqPageSize);
    const startIndex = (faqPage - 1) * faqPageSize;
    const displayItems = filteredFaqItems;

    return (
      <>
        <HeaderCard>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              FAQs
            </Typography>
            <Muted variant="body2">Manage frequently asked questions</Muted>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => handleFaqOpenDialog()}
            >
              Add FAQ
            </Button>
            <Button 
              startIcon={<Refresh />} 
              variant="outlined" 
              onClick={() => fetchFaqItems(faqPage, faqPageSize)} 
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>
        </HeaderCard>

        {/* Search Input */}
        <Box sx={{ mb: 2 }}>
          <SearchInput
            bordered={false}
            placeholder="Search by question, category, or subcategory..."
            onChange={(e) => setFaqSearchQuery(e.target.value)}
            value={faqSearchQuery}
            sx={{
              width: "100%",
              maxWidth: 500,
            }}
          />
        </Box>

        <TableCard>
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
                <TableRow>
                  <StyledTableCell className="header">Sr No</StyledTableCell>
                  <StyledTableCell className="header">Question</StyledTableCell>
                  <StyledTableCell className="header">Category</StyledTableCell>
                  <StyledTableCell className="header">Subcategory</StyledTableCell>
                  <StyledTableCell className="header">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : displayItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {faqSearchQuery ? "No FAQs found matching your search." : "No FAQs found."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayItems.map((item, index) => {
                    // Find original index for proper serial number
                    const originalIndex = faqItems.findIndex(faq => faq.id === item.id);
                    const displayIndex = originalIndex >= 0 ? originalIndex : index;
                    return (
                    <StyledTableRow key={item.id}>
                      <StyledTableCell>
                        <Chip 
                          label={`#${startIndex + displayIndex + 1}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" sx={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {item.question || item.prompt || "N/A"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2">
                          {item.category_name || "N/A"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2">
                          {item.sub_category_name || "N/A"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleFaqOpenDialog(item)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => handleFaqDelete(item.id)}
                                disabled={faqDeletingId === item.id}
                              >
                                {faqDeletingId === item.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <Delete />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>

        {/* Pagination - Only show when not searching */}
        {!faqSearchQuery && faqTotalCount > 0 && (
          <Stack alignItems="center" marginY={3}>
            <AppPagination
              shape="rounded"
              count={totalPages}
              page={faqPage}
              onChange={handleFaqPageChange}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {startIndex + 1} - {Math.min(startIndex + faqItems.length, faqTotalCount)} of {faqTotalCount} FAQs
            </Typography>
          </Stack>
        )}
        
        {/* Search Results Info */}
        {faqSearchQuery && (
          <Stack alignItems="center" marginY={2}>
            <Typography variant="body2" color="text.secondary">
              {displayItems.length > 0 
                ? `Found ${displayItems.length} FAQ${displayItems.length > 1 ? 's' : ''} matching "${faqSearchQuery}"`
                : `No FAQs found matching "${faqSearchQuery}"`
              }
            </Typography>
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Categories" />
          <Tab label="Subcategories" />
          <Tab label="FAQs" />
        </Tabs>
      </Box>

      {currentTab === 0 && renderCategoryTab()}
      {currentTab === 1 && renderSubcategoryTab()}
      {currentTab === 2 && renderFaqTab()}

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={handleCategoryCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {categoryEditingId ? "Edit Category" : "Add Category"}
            </Typography>
            <IconButton onClick={handleCategoryCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <TextField
              label="Category Name *"
              value={categoryFormData.name}
              onChange={(e) => {
                setCategoryFormData((prev) => ({ ...prev, name: e.target.value }));
              }}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCategoryCloseDialog} disabled={categorySaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCategorySave}
            variant="contained"
            disabled={categorySaving || !categoryFormData.name?.trim()}
            startIcon={categorySaving ? <CircularProgress size={16} /> : <Save />}
          >
            {categorySaving ? "Saving..." : categoryEditingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={subcategoryDialogOpen} onClose={handleSubcategoryCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {subcategoryEditingId ? "Edit Subcategory" : "Add Subcategory"}
            </Typography>
            <IconButton onClick={handleSubcategoryCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category *</InputLabel>
              <Select
                value={subcategoryFormData.category_id}
                onChange={(e) => {
                  setSubcategoryFormData((prev) => ({ ...prev, category_id: e.target.value }));
                }}
                label="Category *"
                disabled={loadingCategories}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Subcategory Name *"
              value={subcategoryFormData.name}
              onChange={(e) => {
                setSubcategoryFormData((prev) => ({ ...prev, name: e.target.value }));
              }}
              fullWidth
            />
            <TextField
              label="Sequence"
              type="number"
              value={subcategoryFormData.sequence}
              onChange={(e) => {
                setSubcategoryFormData((prev) => ({ ...prev, sequence: e.target.value }));
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubcategoryCloseDialog} disabled={subcategorySaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubcategorySave}
            variant="contained"
            disabled={subcategorySaving || !subcategoryFormData.name?.trim() || !subcategoryFormData.category_id}
            startIcon={subcategorySaving ? <CircularProgress size={16} /> : <Save />}
          >
            {subcategorySaving ? "Saving..." : subcategoryEditingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={faqDialogOpen} onClose={handleFaqCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {faqEditingId ? "Edit FAQ" : "Add FAQ"}
            </Typography>
            <IconButton onClick={handleFaqCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category *</InputLabel>
              <Select
                value={faqFormData.category_id || ""}
                onChange={(e) => {
                  const value = String(e.target.value);
                  setFaqFormData((prev) => {
                    if (prev.category_id === value) return prev;
                    const newData = { ...prev, category_id: value, subcategory_id: "" };
                    // Fetch subcategories after state update
                    if (value) {
                      setTimeout(() => fetchSubcategories(Number(value)), 0);
                    }
                    return newData;
                  });
                }}
                label="Category *"
                disabled={loadingCategories}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={String(category.id)}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subcategory *</InputLabel>
              <Select
                value={faqFormData.subcategory_id || ""}
                onChange={(e) => {
                  setFaqFormData((prev) => ({ ...prev, subcategory_id: String(e.target.value) }));
                }}
                label="Subcategory *"
                disabled={!faqFormData.category_id || loadingSubcategories}
              >
                {!faqFormData.category_id ? (
                  <MenuItem disabled>Please select a category first</MenuItem>
                ) : (
                  subcategories.map((subcategory) => (
                    <MenuItem key={subcategory.id} value={String(subcategory.id)}>
                      {subcategory.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              label="Question *"
              multiline
              rows={4}
              value={faqFormData.question}
              onChange={(e) => {
                setFaqFormData((prev) => ({ ...prev, question: e.target.value }));
              }}
              fullWidth
              placeholder="Enter FAQ question..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFaqCloseDialog} disabled={faqSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleFaqSave}
            variant="contained"
            disabled={faqSaving || !faqFormData.question?.trim() || !faqFormData.category_id || !faqFormData.subcategory_id}
            startIcon={faqSaving ? <CircularProgress size={16} /> : <Save />}
          >
            {faqSaving ? "Saving..." : faqEditingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default DiseaseAdvisory;

